import { formatInTimeZone } from 'date-fns-tz';

import { getBrowserTimezone } from '@/components/ui/date-time-picker/utils';

// Human wording for admin-api's validation codes. The same code set arrives two
// ways: as `publication_failure_code` on a stored gift or season pass, and as
// `reason.code` in a 422 body. The backend owns the codes, so an unmapped one
// falls back to the raw string rather than rendering empty. One map = one place
// to swap for i18n keys.
const PUBLICATION_FAILURE_LABELS: Record<string, string> = {
  start_date_in_past: 'Start date is in the past',
  expire_date_in_past: 'Expiration date is in the past',
  not_on_period_boundary: 'End date does not land on a period boundary',
  no_occurrence: 'The window leaves room for no occurrence',
  reward_quantity_exceeds_limit: 'Reward quantity exceeds the item limit',
};

export const publicationFailureLabel = (code?: string): string | undefined =>
  code ? (PUBLICATION_FAILURE_LABELS[code] ?? code) : undefined;

/**
 * The machine-readable half of a 422 body: which rule failed, on which field,
 * and — when the rule can name one — a value that would satisfy it.
 */
export interface ApiErrorReason {
  code: string;
  field: string;
  hint?: string;
}

export const isApiErrorReason = (value: unknown): value is ApiErrorReason =>
  typeof value === 'object' &&
  value !== null &&
  'code' in value &&
  typeof (value as { code: unknown }).code === 'string';

// Gift windows are stored as UTC instants and carry no timezone of their own, so
// a hint is only meaningful once it is put on the viewer's clock. The offset
// label is not decoration: a campaign's local renewal hour shifts across a DST
// change, and without it the operator reads 10:00 one week and 11:00 the next
// and concludes the backend moved their gift.
const formatHint = (hint: string): string | undefined => {
  const at = new Date(hint);
  if (Number.isNaN(at.getTime())) return undefined;
  // 24h like every other date on screen — never the locale `p` token, which is AM/PM.
  return formatInTimeZone(at, getBrowserTimezone(), 'PP, HH:mm (OOOO)');
};

/**
 * Human wording for a 422 `reason`. Explains the rule first, then gives the date
 * the operator can act on — the bare rule is not actionable, and the bare date
 * does not say why theirs was rejected.
 */
export const validationReasonMessage = (
  reason?: ApiErrorReason,
): string | undefined => {
  if (!reason?.code) return undefined;

  const at = reason.hint ? formatHint(reason.hint) : undefined;
  if (reason.code === 'not_on_period_boundary' && at) {
    return `This gift renews at its start time. Nearest valid end: ${at}.`;
  }

  return publicationFailureLabel(reason.code);
};
